import { useEffect, useRef, useState } from "react";
import type { Note } from "../api";
import { markdownToText } from "../markdown";
import { useShare, useShareMutations } from "../queries";

export function shareUrl(token: string): string {
  return `${window.location.origin}/s/${token}`;
}

const canNativeShare = () => typeof navigator !== "undefined" && typeof navigator.share === "function";

export function ShareDialog({ note, onClose }: { note: Note; onClose: () => void }) {
  const share = useShare(note.id);
  const { create, revoke } = useShareMutations(note.id);
  const [copied, setCopied] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const link = share.data ? shareUrl(share.data.token) : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    dialogRef.current?.querySelector<HTMLElement>("button, input")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const flash = (what: string) => {
    setCopied(what);
    setTimeout(() => setCopied(null), 1600);
  };

  /** The note as plain text for messages: title, then the body (Markdown turned into readable text). */
  const asText = () => {
    const bodyText = note.format === "markdown" ? markdownToText(note.bodyText) : note.bodyText;
    return `${note.title}\n\n${bodyText}`.trim();
  };

  const sendVia = async (withLink: boolean) => {
    const data: ShareData = withLink && link ? { title: note.title, url: link } : { title: note.title, text: asText() };
    if (canNativeShare()) {
      try {
        await navigator.share(data);
      } catch {
        // Cancelled by the person; nothing to do.
      }
      return;
    }
    await navigator.clipboard.writeText(withLink && link ? link : asText());
    flash(withLink ? "link" : "text");
  };

  return (
    <div className="modal-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="share-title" ref={dialogRef}>
        <header className="modal-head">
          <h2 id="share-title">Share “{note.title || "Untitled"}”</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </header>

        <section className="share-section">
          <h3>Public link</h3>
          {share.isPending ? (
            <p className="muted">Loading…</p>
          ) : link ? (
            <>
              <div className="link-row">
                <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="Share link" />
                <button
                  type="button"
                  className="btn btn-primary btn-small"
                  onClick={async () => {
                    await navigator.clipboard.writeText(link);
                    flash("link");
                  }}
                >
                  {copied === "link" ? "Copied" : "Copy link"}
                </button>
              </div>
              <p className="muted small">
                Anyone with the link can read this note. Attachments stay private.
                {share.data && ` Viewed ${share.data.viewCount} ${share.data.viewCount === 1 ? "time" : "times"}.`}
              </p>
              <div className="share-actions">
                {canNativeShare() && (
                  <button type="button" className="btn btn-small" onClick={() => sendVia(true)}>
                    Send link via…
                  </button>
                )}
                <button type="button" className="btn btn-small btn-danger" onClick={() => revoke.mutate()} disabled={revoke.isPending}>
                  Stop sharing
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="muted small">
                Create a read-only link anyone can open, no account needed. You can turn it off at any time. Attachments stay private.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => create.mutate()}
                disabled={create.isPending || note.status !== "active"}
              >
                {create.isPending ? "Creating…" : "Create public link"}
              </button>
              {note.status !== "active" && <p className="muted small">Only notes in your notes can be shared, not archived or trashed ones.</p>}
              {create.isError && <p className="alert">{create.error.message}</p>}
            </>
          )}
        </section>

        <section className="share-section">
          <h3>Send as text</h3>
          <p className="muted small">Send the note's text in a message or email, without making a link.</p>
          <button type="button" className="btn btn-small" onClick={() => sendVia(false)}>
            {canNativeShare() ? "Send via…" : copied === "text" ? "Copied" : "Copy text"}
          </button>
        </section>
      </div>
    </div>
  );
}
